import React, { useState } from "react";

import OverviewDaRegiao from "./OverviewDaRegiao";
import { regioesDisponiveis } from "../TrilhaLudica";

/**
 * A escolha da região da trilha, em cartões com o mascote.
 *
 * Era um `<select>` de texto ("Norte — Amazônia"), o que escondia o tamanho da
 * decisão: a região define o CENÁRIO que o aluno percorre, o MASCOTE que fala
 * com ele e as 22 CARTAS que ele vai colecionar. Nada disso cabe num rótulo.
 *
 * A gramática é a da escolha de explorador do próprio aluno
 * (`TrilhaLudica/nucleo/SelecaoAvatar.jsx`): botão com `aria-pressed`, figura
 * grande, nome, e um selo de check no escolhido. Só a gramática — o conteúdo é
 * outro. O explorador é quem o ALUNO é, e são os mesmos quatro em toda região; o
 * mascote é da REGIÃO, e ninguém escolhe: vem junto com ela.
 *
 * A cor de cada cartão sai da paleta da própria região, e não de uma tabela
 * daqui. É o que faz a região nova aparecer certa sem ninguém tocar neste
 * arquivo — acrescentar região continua sendo encher uma pasta no módulo.
 *
 * @param {string}   valor       id da região selecionada
 * @param {Function} onEscolher  recebe o id da região clicada
 * @param {boolean}  travada     modo edição: a região não muda depois da criação
 * @param {string}   aviso       o que dizer sob os cartões
 */
function EscolhaDeRegiao({ valor, onEscolher, travada = false, aviso }) {
  // Qual região o professor está espiando. Fica AQUI e não no ActivityCreator
  // porque é estado de interface pura: nada do que o overview mostra é escolha,
  // e fechá-lo não muda a trilha.
  const [espiando, setEspiando] = useState(null);
  const regioes = regioesDisponiveis();

  return (
    <div className="ct-regiao">
      <div className="ct-regiao__grade">
        {regioes.map(regiao => {
          const escolhida = !travada && regiao.id === valor;

          return (
            // O olho é um botão IRMÃO do cartão, e não um filho: botão dentro de
            // botão é HTML inválido, e o clique no de dentro dispararia os dois.
            <div className="ct-regiao__caixa" key={regiao.id}>
              <button
                type="button"
                className="ct-regiao__olho"
                onClick={() => setEspiando(regiao.id)}
                title={`Ver o que a trilha do ${regiao.nome} entrega`}
                aria-label={`Ver as cartas, o cenário e o mascote da região ${regiao.nome} — ${regiao.bioma}`}
              >
                <i className="fa fa-eye" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`ct-regiao__cartao${
                  escolhida ? " ct-regiao__cartao--escolhida" : ""
                }`}
                // O estado da escolha vai no `aria-pressed`, e não numa cor:
                // quem usa leitor de tela ouve "pressionado" em vez de nada.
                aria-pressed={escolhida}
                disabled={travada}
                onClick={() => onEscolher(regiao.id)}
                style={{
                  // As cores vêm do descritor da região (ver `regioesDisponiveis`).
                  // Como variáveis, e não em `background` direto, porque o CSS
                  // precisa delas em três lugares (disco, borda e selo).
                  //
                  // O par que pinta borda, texto e selo é o CROMO, não o accent: o
                  // cartão fica sobre branco, que é a situação para a qual o cromo
                  // foi escolhido em cada região. No Norte isso é a diferença entre
                  // um cartão verde-Amazônia e um cartão dourado.
                  "--ct-regiao-cor": regiao.cores.chromeBorda,
                  "--ct-regiao-cor-forte": regiao.cores.chromeTexto,
                  "--ct-regiao-cor-clara": regiao.cores.chromeFundo,
                  "--ct-regiao-fundo-topo": regiao.cores.fundoTopo,
                  "--ct-regiao-fundo-base": regiao.cores.fundoBase
                }}
              >
                <span className="ct-regiao__disco">
                  {/*
                  A arte é RGBA transparente (o módulo proíbe cabeça com fundo em
                  disco, senão vira um círculo branco no medalhão do nó), então
                  quem pinta o fundo é o `.ct-regiao__disco`.
                  `alt=""` porque o nome do mascote está escrito logo abaixo: a
                  imagem repetiria o que o leitor de tela já vai ler.
                */}
                  <img src={regiao.mascote.cabeca} alt="" aria-hidden="true" />
                </span>
                <span className="ct-regiao__nome">{regiao.nome}</span>
                <span className="ct-regiao__bioma">{regiao.bioma}</span>
                <span className="ct-regiao__mascote">
                  {regiao.mascote.nome}
                </span>
                {escolhida && (
                  <i
                    className="fa fa-check ct-regiao__selo"
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
          );
        })}
      </div>
      {aviso && <small className="text-muted">{aviso}</small>}

      {espiando && (
        <OverviewDaRegiao
          regiaoId={espiando}
          onFechar={() => setEspiando(null)}
        />
      )}
    </div>
  );
}

export default EscolhaDeRegiao;
